"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.signedAdjustmentAmount = signedAdjustmentAmount;
exports.invoiceCapturedVersusMilestoneBudgets = invoiceCapturedVersusMilestoneBudgets;
exports.sumAssignedInvoiceAmount = sumAssignedInvoiceAmount;
exports.adjustmentKindLabel = adjustmentKindLabel;
exports.formatAdjustmentCode = formatAdjustmentCode;
exports.parseAdjustmentSeq = parseAdjustmentSeq;
exports.nextAdjustmentSeq = nextAdjustmentSeq;
exports.extractAdjustmentDocumentIds = extractAdjustmentDocumentIds;
exports.mapAdjustmentToDocumentsHubVariation = mapAdjustmentToDocumentsHubVariation;
exports.adjustmentDisplayTitle = adjustmentDisplayTitle;
exports.progressVariationBucket = progressVariationBucket;
exports.invoiceAssignmentLabel = invoiceAssignmentLabel;
exports.adjustmentsAssignedToInvoice = adjustmentsAssignedToInvoice;
exports.invoiceAdjustmentLines = invoiceAdjustmentLines;
exports.deductionStatusDisplay = deductionStatusDisplay;
exports.buildProgressVariationGroups = buildProgressVariationGroups;
const round_money_js_1 = require("../money/round-money.js");
/** Stored amounts are unsigned; sign by kind for contract / invoice math. */
function signedAdjustmentAmount(adjustment) {
    const abs = Math.abs(Number(adjustment.amount || 0));
    return adjustment.kind === "reduction" ? -abs : abs;
}
/** Net of invoice captured vs listed milestone budgets. Positive is additions, negative is deductions. */
function invoiceCapturedVersusMilestoneBudgets(captured, milestoneBudgets) {
    const listed = (0, round_money_js_1.roundMoney)(milestoneBudgets.reduce((sum, amount) => sum + Number(amount || 0), 0));
    const net = (0, round_money_js_1.roundMoney)(Number(captured || 0) - listed);
    if (Math.abs(net) < 1e-9)
        return null;
    return { amount: net, label: net < 0 ? "Deductions" : "Variations" };
}
/** Invoice amount = assigned milestone budgets + signed adjustments (reductions subtract). */
function sumAssignedInvoiceAmount(milestones, adjustments, milestoneIds, adjustmentIds) {
    const milestoneTotal = milestones
        .filter((milestone) => milestoneIds.includes(milestone.id))
        .reduce((sum, milestone) => sum + Number(milestone.budgetAmount || 0), 0);
    const adjustmentTotal = adjustments
        .filter((adjustment) => adjustmentIds.includes(adjustment.id))
        .reduce((sum, adjustment) => sum + signedAdjustmentAmount(adjustment), 0);
    return (0, round_money_js_1.roundMoney)(milestoneTotal + adjustmentTotal);
}
function adjustmentKindLabel(kind) {
    return kind === "reduction" ? "Deduction Order" : "Variation Order";
}
function formatAdjustmentCode(kind, seq) {
    const prefix = kind === "reduction" ? "DO" : "VO";
    return `${prefix}-${String(Math.max(1, Math.floor(seq))).padStart(2, "0")}`;
}
function parseAdjustmentSeq(code, kind) {
    const prefix = kind === "reduction" ? "DO" : "VO";
    const match = new RegExp(`^${prefix}-(\\d+)$`, "i").exec((code ?? "").trim());
    if (!match)
        return null;
    const seq = Number(match[1]);
    return Number.isFinite(seq) && seq > 0 ? seq : null;
}
function nextAdjustmentSeq(codes, kind) {
    let max = 0;
    for (const code of codes) {
        const seq = parseAdjustmentSeq(code, kind);
        if (seq != null && seq > max)
            max = seq;
    }
    return max + 1;
}
/** Document IDs stored on `project_adjustments.documents` (string ids or `{ id }` objects). */
function extractAdjustmentDocumentIds(documents) {
    if (!Array.isArray(documents))
        return [];
    const ids = [];
    for (const entry of documents) {
        if (typeof entry === "string" && entry.trim()) {
            ids.push(entry.trim());
            continue;
        }
        if (entry && typeof entry === "object" && typeof entry.id === "string") {
            const id = entry.id.trim();
            if (id)
                ids.push(id);
        }
    }
    return ids;
}
/** Documents-hub Variation group fields for a `project_adjustments` row (evidence lens). */
function mapAdjustmentToDocumentsHubVariation(adjustment) {
    const code = (adjustment.displayCode || "").trim() || formatAdjustmentCode(adjustment.kind, 1);
    const title = (adjustment.description || "").trim() || adjustmentKindLabel(adjustment.kind);
    return {
        id: adjustment.id,
        code,
        title,
        evidenceIds: extractAdjustmentDocumentIds(adjustment.documents),
    };
}
function adjustmentDisplayTitle(kind, displayCode) {
    const label = adjustmentKindLabel(kind);
    const code = displayCode?.trim();
    return code ? `${label} ${code}` : label;
}
const GROUP_TITLES = {
    accepted: "Accepted",
    pending_review: "Pending client review",
    draft: "Draft not sent",
};
const STATUS_LABELS = {
    accepted: "Accepted",
    pending_review: "Pending client review",
    draft: "Draft not sent",
};
function progressVariationBucket(adjustment, pendingRevisionId) {
    if (adjustment.status === "accepted")
        return "accepted";
    if (pendingRevisionId && adjustment.revisionId === pendingRevisionId)
        return "pending_review";
    return "draft";
}
function invoiceAssignmentLabel(invoice) {
    const name = invoice?.label?.trim();
    if (!name)
        return "Not yet on an invoice";
    return name.toLowerCase().startsWith("invoice") ? `On ${name}` : `On invoice ${name}`;
}
function adjustmentsAssignedToInvoice(adjustments, invoiceId) {
    if (!invoiceId)
        return [];
    return adjustments.filter((item) => item.invoiceId === invoiceId);
}
function invoiceAdjustmentLines(adjustments, invoiceId, options) {
    return adjustmentsAssignedToInvoice(adjustments, invoiceId)
        .filter((item) => !options?.acceptedOnly || item.status === "accepted")
        .map((item) => ({
        id: item.id,
        name: adjustmentDisplayTitle(item.kind, item.displayCode),
        amount: signedAdjustmentAmount({ amount: Number(item.amount || 0), kind: item.kind }),
    }));
}
/** Accepted deductions share one tag; tooltip distinguishes unpaid vs paid crediting invoice. */
function deductionStatusDisplay(invoice) {
    const name = invoice?.label?.trim() || "invoice";
    const label = `Deducted from ${name}`;
    if (invoice?.paid) {
        const paidOn = invoice.paidAt
            ? new Date(invoice.paidAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
            : null;
        return {
            statusLabel: label,
            statusTooltip: paidOn
                ? `${name} was paid on ${paidOn}. The deduction has been applied`
                : `${name} has been paid. The deduction has been applied`,
        };
    }
    return {
        statusLabel: label,
        statusTooltip: `${name} has not been paid yet. The deduction will be successfully applied once it has been paid`,
    };
}
function buildProgressVariationGroups(adjustments, pendingRevisionId, invoiceById) {
    const groups = {
        accepted: [],
        pending_review: [],
        draft: [],
    };
    for (const adjustment of adjustments) {
        const bucket = progressVariationBucket(adjustment, pendingRevisionId);
        const invoice = adjustment.invoiceId ? invoiceById?.[adjustment.invoiceId] : null;
        let statusLabel = STATUS_LABELS[bucket];
        let statusTooltip = null;
        if (bucket === "accepted" && adjustment.kind === "reduction") {
            const deducted = deductionStatusDisplay(invoice);
            statusLabel = deducted.statusLabel;
            statusTooltip = deducted.statusTooltip;
        }
        groups[bucket].push({
            id: adjustment.id,
            kind: adjustment.kind,
            displayCode: adjustment.displayCode ?? "",
            title: adjustmentDisplayTitle(adjustment.kind, adjustment.displayCode),
            description: adjustment.description ?? "",
            amount: Math.abs(Number(adjustment.amount || 0)),
            bucket,
            statusLabel,
            statusTooltip,
            invoiceLabel: invoiceAssignmentLabel(invoice),
        });
    }
    return ["accepted", "pending_review", "draft"]
        .map((id) => ({ id, title: GROUP_TITLES[id], items: groups[id] }))
        .filter((group) => group.items.length > 0);
}
